import { get, isEqual, set, isArray, mergeWith } from 'lodash';
import { optionalRequiredFormFieldsFallback } from '../../../utils/configurationFallbacks';
import {
    removeArrayDuplicatesInObject,
    getPricingDisplayTaxAndFeeOption,
    getInContextBookingStatusMigrationOverride
} from '../../../utils/configuration-utils';
import * as ActionTypes from '../../../constants/ActionTypes';
import { configurationSystemDefault } from '../../../constants/SystemDefaults';
import { handleActions } from 'redux-actions';
import { arrayMove } from 'react-sortable-hoc';
import { CRM_SIGN_IN_TYPE } from '../../../constants/crm-sign-in-type';

const configurationSectionDataStoreDefaults = {
    currentConfig: '',
    currentConfigCopy: configurationSystemDefault,
    customContentLanguageCode: 'en-US',
    configList: []
};

const setValueForCodeInConfig = (state, action) => {
    const { code, value } = action;
    const currentConfigCopy = _.cloneDeep(state.currentConfigCopy);
    _.set(currentConfigCopy, `${code}`, value);

    return { ...state, currentConfigCopy };
};

const unsetValueForCodeInConfig = (state, action) => {
    const { code } = action;
    const currentConfigCopy = _.cloneDeep(state.currentConfigCopy);
    _.unset(currentConfigCopy, `${code}`);

    return { ...state, currentConfigCopy };
};

const mergeArrayCallback = (objValue, srcValue) => {
    if (isArray(srcValue)) {
        return srcValue;
    }
};

const configurationSectionDataStore = handleActions(
    {
        [ActionTypes.CHANGE_DISPLAYED_FIELDS]: (state, action) => {
            const { fieldCode, formName } = action;

            let displayedFieldsList = state.currentConfigCopy.displayedFields[formName];
            let requiredFieldsList = state.currentConfigCopy.requiredFields[formName];

            if (
                displayedFieldsList.includes(fieldCode) ||
                (fieldCode === 'cvvCode' && state.currentConfigCopy.requiredFieldList.includes(fieldCode))
            ) {
                displayedFieldsList = displayedFieldsList.filter(item => item !== fieldCode);
                requiredFieldsList = requiredFieldsList.filter(item => item !== fieldCode);
            } else {
                displayedFieldsList = [...displayedFieldsList, fieldCode];

                if (fieldCode === 'cvvCode' && !requiredFieldsList.includes(fieldCode)) {
                    requiredFieldsList.push('cvvCode');
                }
            }

            const optionalFieldList =
                formName === 'guestDetails'
                    ? _.differenceWith(displayedFieldsList, requiredFieldsList)
                    : state.currentConfigCopy.optionalFieldList;
            const requiredFieldList =
                formName === 'guestDetails' ? requiredFieldsList : state.currentConfigCopy.requiredFieldList;

            return {
                ...state,
                currentConfigCopy: {
                    ...state.currentConfigCopy,
                    optionalFieldList,
                    requiredFieldList,
                    displayedFields: {
                        ...state.currentConfigCopy.displayedFields,
                        [formName]: displayedFieldsList
                    },
                    requiredFields: {
                        ...state.currentConfigCopy.requiredFields,
                        [formName]: requiredFieldsList
                    }
                }
            };
        },
        [ActionTypes.CHANGE_REQUIRED_FIELDS]: (state, action) => {
            const { fieldCode, formName } = action;

            let displayedFieldsList = state.currentConfigCopy.displayedFields[formName];
            let requiredFieldsList = state.currentConfigCopy.requiredFields[formName];

            if (requiredFieldsList.includes(fieldCode)) {
                requiredFieldsList = requiredFieldsList.filter(item => item !== fieldCode);
            } else {
                if (!displayedFieldsList.includes(fieldCode)) {
                    displayedFieldsList = [...displayedFieldsList, fieldCode];
                }

                requiredFieldsList = [...requiredFieldsList, fieldCode];
            }

            const optionalFieldList =
                formName === 'guestDetails'
                    ? _.differenceWith(displayedFieldsList, requiredFieldsList)
                    : state.currentConfigCopy.optionalFieldList;
            const requiredFieldList =
                formName === 'guestDetails' ? requiredFieldsList : state.currentConfigCopy.requiredFieldList;

            return {
                ...state,
                optionalFieldList,
                requiredFieldList,
                currentConfigCopy: {
                    ...state.currentConfigCopy,
                    displayedFields: {
                        ...state.currentConfigCopy.displayedFields,
                        [formName]: displayedFieldsList
                    },
                    requiredFields: {
                        ...state.currentConfigCopy.requiredFields,
                        [formName]: requiredFieldsList
                    },
                    requiredFieldList
                }
            };
        },
        [ActionTypes.MAKE_VISIBLE_FILTERS]: (state, action) => {
            const code = action.code;
            const key = action.key;
            const list = state.currentConfigCopy[key];
            const index = list.indexOf(code);
            list.splice(index, 1);
            let obj = {
                currentConfigCopy: {
                    ...state.currentConfigCopy
                }
            };
            obj['currentConfigCopy'] = { ...state.currentConfigCopy };
            obj['currentConfigCopy'] = { ...state.currentConfigCopy, [key]: list };
            return { ...state, ...obj };
        },
        [ActionTypes.MAKE_INVISIBLE_FILTERS]: (state, action) => {
            const code = action.code;
            const key = action.key;
            let list = state.currentConfigCopy[key];
            list.push(code);
            let obj = {
                currentConfigCopy: {
                    ...state.currentConfigCopy
                }
            };
            obj['currentConfigCopy'] = { ...state.currentConfigCopy };
            obj['currentConfigCopy'] = { ...state.currentConfigCopy, [key]: list };
            return { ...state, ...obj };
        },
        [ActionTypes.SET_ARRAY_OR_OBJECT_IN_CONFIG]: setValueForCodeInConfig,
        [ActionTypes.SET_BOOLEAN_VALUE_IN_CONFIG]: setValueForCodeInConfig,
        [ActionTypes.SET_NUMBER_VALUE_IN_CONFIG]: setValueForCodeInConfig,
        [ActionTypes.SET_TEXT_VALUE_IN_CONFIG]: setValueForCodeInConfig,
        [ActionTypes.UNSET_VALUE_IN_CONFIG]: unsetValueForCodeInConfig,
        [ActionTypes.SET_LIST_VALUE_IN_CONFIG]: setValueForCodeInConfig,
        [ActionTypes.TOGGLE_BOOLEAN_VALUE_IN_CONFIG]: (state, action) =>
            setValueForCodeInConfig(state, { ...action, value: !action.value }),
        [ActionTypes.SET_CUSTOM_CONTENT_TEXT_VALUE_IN_CONFIG]: (state, action) => {
            let obj = {
                currentConfigCopy: {
                    ...state.currentConfigCopy
                }
            };
            const { code, value, languageCode } = action;
            obj['currentConfigCopy'] = { ...state.currentConfigCopy };
            obj['currentConfigCopy'][action.parent] = { ...state.currentConfigCopy[action.parent] };

            if (!action.side) {
                if (languageCode) {
                    obj['currentConfigCopy'][action.parent][languageCode] = {
                        ...state.currentConfigCopy[action.parent][languageCode]
                    };
                    obj['currentConfigCopy'][action.parent][languageCode][code] = value;
                } else if (action.code) {
                    obj['currentConfigCopy'][action.parent] = { ...state.currentConfigCopy[action.parent] };
                    obj['currentConfigCopy'][action.parent][code] = value;
                } else {
                    if (typeof value === 'object') {
                        obj['currentConfigCopy'][action.parent] = {
                            ...state.currentConfigCopy[action.parent],
                            ...value
                        };
                    } else {
                        obj['currentConfigCopy'][action.parent] = value;
                    }
                }
            } else {
                obj['currentConfigCopy'][action.parent][languageCode] = {
                    ...state.currentConfigCopy[action.parent][languageCode]
                };
                obj['currentConfigCopy'][action.parent][languageCode][action.side] = {
                    ...obj['currentConfigCopy'][action.parent][languageCode][action.side]
                };
                obj['currentConfigCopy'][action.parent][languageCode][action.side][code] = value;
            }
            return { ...state, ...obj };
        },
        [ActionTypes.ADD_CONFIG_ROW]: (state, action) => {
            let obj = {
                currentConfigCopy: {
                    ...state.currentConfigCopy
                }
            };
            const code = action.code;
            if (code === 'videoPaths') {
                let videoPathList = state.currentConfigCopy.videoPaths;
                let newObj = {
                    Code: '',
                    Path: action.path
                };
                videoPathList.push(newObj);
                obj['currentConfigCopy'] = { ...state.currentConfigCopy };
                obj['currentConfigCopy']['videoPaths'] = videoPathList;
            } else if (code === 'hotelMapSettings') {
                let mapOnChainPageDestinations = state.currentConfigCopy.mapOnChainPage.destinations;
                const newObj = {
                    label: '',
                    location: {
                        lat: 0,
                        lng: 0,
                        zoom: 1,
                        radius: 1
                    }
                };
                mapOnChainPageDestinations.push(newObj);
                _.set(obj, 'currentConfigCopy.mapOnChainPage.destinations', mapOnChainPageDestinations);
            } else if (code === 'loyaltyAssignments') {
                let loyaltyAssignments = state.currentConfigCopy.signInForBenefits.loyaltyAssignments;
                let newObj = {
                    programId: '',
                    levelCode: ''
                };
                loyaltyAssignments.push(newObj);
                obj['currentConfigCopy'] = { ...state.currentConfigCopy };
                obj['currentConfigCopy']['signInForBenefits'] = { ...state.currentConfigCopy.signInForBenefits };
                obj['currentConfigCopy']['signInForBenefits']['loyaltyAssignments'] = loyaltyAssignments;
            }
            return { ...state, ...obj };
        },
        [ActionTypes.CHANGE_CONFIG_ROW]: (state, action) => {
            let obj = {
                currentConfigCopy: {
                    ...state.currentConfigCopy
                }
            };
            let mapOnChainPageDestinations = _.get(state, 'currentConfigCopy.mapOnChainPage.destinations', []);
            _.set(
                obj,
                'currentConfigCopy.mapOnChainPage.destinations',
                arrayMove(mapOnChainPageDestinations, action.oldIndex, action.newIndex)
            );

            return { ...state, ...obj };
        },
        [ActionTypes.EDIT_CONFIG_ROW]: (state, action) => {
            let obj = {
                currentConfigCopy: {
                    ...state.currentConfigCopy
                }
            };
            const code = action.parentCode;
            const editCode = action.editCode;
            if (code === 'videoPaths') {
                let videoPaths = state.currentConfigCopy.videoPaths;
                let newObj = videoPaths[action.index];
                newObj[action.editCode] = action.value;
                videoPaths.splice(action.index, 1, newObj);
                obj['currentConfigCopy'] = { ...state.currentConfigCopy };
                obj['currentConfigCopy']['videoPaths'] = videoPaths;
            } else if (code === 'hotelMapSettings') {
                let mapOnChainPageDestinations = state.currentConfigCopy.mapOnChainPage.destinations[action.index];
                _.set(mapOnChainPageDestinations, action.editCode, action.value);
            } else if (code === 'loyaltyAssignments') {
                let loyaltyAssignments = state.currentConfigCopy.signInForBenefits.loyaltyAssignments;
                let newObj = loyaltyAssignments[action.index];
                newObj[action.editCode] = action.value;
                loyaltyAssignments.splice(action.index, 1, newObj);
                obj['currentConfigCopy'] = { ...state.currentConfigCopy };
                obj['currentConfigCopy']['signInForBenefits'] = { ...state.currentConfigCopy.signInForBenefits };
                obj['currentConfigCopy']['signInForBenefits']['loyaltyAssignments'] = loyaltyAssignments;
            }
            return { ...state, ...obj };
        },
        [ActionTypes.DELETE_CONFIG_ROW]: (state, action) => {
            let obj = {
                currentConfigCopy: {
                    ...state.currentConfigCopy
                }
            };
            const code = action.parentCode;
            if (code === 'videoPaths') {
                let videoPaths = state.currentConfigCopy.videoPaths;
                videoPaths.splice(action.index, 1);
                obj['currentConfigCopy'] = { ...state.currentConfigCopy };
                obj['currentConfigCopy']['videoPaths'] = videoPaths;
            } else if (code === 'hotelMapSettings') {
                let mapOnChainPageDestinations = state.currentConfigCopy.mapOnChainPage.destinations;
                mapOnChainPageDestinations.splice(action.index, 1);
                _.set(obj, 'currentConfigCopy.mapOnChainPage.destinations', mapOnChainPageDestinations);
            } else if (code === 'loyaltyAssignments') {
                let loyaltyAssignments = state.currentConfigCopy.signInForBenefits.loyaltyAssignments;
                loyaltyAssignments.splice(action.index, 1);
                obj['currentConfigCopy'] = { ...state.currentConfigCopy };
                obj['currentConfigCopy']['signInForBenefits'] = { ...state.currentConfigCopy.signInForBenefits };
                obj['currentConfigCopy']['signInForBenefits']['loyaltyAssignments'] = loyaltyAssignments;
            }
            return { ...state, ...obj };
        },
        [ActionTypes.CHANGE_CUSTOM_CONTENT_LANGUAGE_CODE]: (state, action) => {
            const { languageCode } = action;
            return { ...state, customContentLanguageCode: languageCode };
        },
        [ActionTypes.LOGIN_SET_CONFIG]: (state, action) => {
            const { code, config } = action;
            const currentConfigCopy = configurationSectionDataStoreDefaults.currentConfigCopy;

            const getCrmSignInType = () => {
                const { NONE, PROFILE_PROVIDER } = CRM_SIGN_IN_TYPE;
                const isCrmEnabled = get(config, 'crm.enabled');
                let crmSignInType = get(config, 'crm.crmSignInType');

                if (isCrmEnabled) {
                    crmSignInType = PROFILE_PROVIDER;
                } else if (isEqual(isCrmEnabled, false)) {
                    crmSignInType = NONE;
                }
                return crmSignInType;
            };
            set(config, 'crm.crmSignInType', getCrmSignInType());
            set(config, 'pricingDisplayTaxAndFeeOption', getPricingDisplayTaxAndFeeOption(config));

            // Backward-compat: when inContextBookingStatus is absent in the loaded config,
            // apply the migration override before merging with system defaults.
            const inContextBookingStatusOverride = getInContextBookingStatusMigrationOverride(config);

            if (!config.displayedFields || !config.requiredFields) {
                const { optionalFieldList, requiredFieldList } = config;
                const fallbackForRequiredFields = optionalRequiredFormFieldsFallback(
                    optionalFieldList,
                    requiredFieldList,
                    currentConfigCopy
                );
                const mergedCurrentConfigCopy = removeArrayDuplicatesInObject(
                    mergeWith({}, currentConfigCopy, config, fallbackForRequiredFields, inContextBookingStatusOverride, mergeArrayCallback)
                );

                return {
                    ...state,
                    configCode: code,
                    currentConfig: config,
                    currentConfigCopy: mergedCurrentConfigCopy
                };
            }

            const mergedCurrentConfigCopy = mergeWith({}, currentConfigCopy, config, inContextBookingStatusOverride, mergeArrayCallback);

            return {
                ...state,
                configCode: code,
                currentConfig: config,
                currentConfigCopy: mergedCurrentConfigCopy
            };
        },
        [ActionTypes.SET_THIRDPARTY_DOMAINS]: state => {
            return { ...state };
        },
        [ActionTypes.REFRESH_SELECTED_CONFIG_FILE]: state => {
            return { ...state, configCode: undefined, configFileToSave: undefined, configFileChanged: undefined };
        },
        [ActionTypes.SET_CONFIG_FILE_READY_TO_SAVE]: (state, action) => {
            return { ...state, configFileChanged: action.configFileChanged };
        },
        [ActionTypes.SET_CONFIG_FILE_DATA]: (state, action) => {
            return { ...state, configFileToSave: action.configToSave };
        }
    },
    configurationSectionDataStoreDefaults
);

export default configurationSectionDataStore;



// WEBPACK FOOTER //
// ./src/reducers/manage/data/configuration.js